export type BarTone = "sky" | "emerald" | "amber" | "red" | "slate";

export interface BarEntry {
  label: string;
  value: number;
}

interface HorizontalBarListProps {
  entries: BarEntry[];
  tone?: BarTone;
  limit?: number;
  emptyMessage?: string;
  valueFormatter?: (n: number) => string;
}

const FILL_CLASS: Record<BarTone, string> = {
  sky: "bg-info/70",
  emerald: "bg-success/70",
  amber: "bg-warning/70",
  red: "bg-danger/70",
  slate: "bg-neutral/70",
};

const TRACK_CLASS: Record<BarTone, string> = {
  sky: "bg-info/10",
  emerald: "bg-success/10",
  amber: "bg-warning/10",
  red: "bg-danger/10",
  slate: "bg-neutral/10",
};

export function HorizontalBarList({
  entries,
  tone = "sky",
  limit = 8,
  emptyMessage = "No data",
  valueFormatter = (n) => n.toLocaleString("en-US"),
}: HorizontalBarListProps) {
  if (entries.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border-subtle px-3 py-6 text-center text-sm text-fg-faint">
        {emptyMessage}
      </div>
    );
  }

  const sorted = [...entries].sort((a, b) => b.value - a.value).slice(0, limit);
  const top = Math.max(1, ...sorted.map((e) => e.value));

  return (
    <ul className="flex flex-col gap-2">
      {sorted.map((e) => {
        const pct = (e.value / top) * 100;
        return (
          <li key={e.label} className="grid grid-cols-[1fr_auto] gap-x-3">
            <div className="min-w-0">
              <div
                className="truncate text-xs text-neutral-text"
                title={e.label}
              >
                {e.label}
              </div>
              <div
                className={`mt-1 h-1.5 w-full overflow-hidden rounded-full ${TRACK_CLASS[tone]}`}
              >
                <div
                  className={`h-full rounded-full ${FILL_CLASS[tone]}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
            <div className="self-center text-xs font-medium tabular-nums text-neutral-text-strong">
              {valueFormatter(e.value)}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
